import {
  CmsMultipleChoiceOptionCollection,
  CmsN1ColumnHeroBanner,
  CmsN2ColumnHeroBanner,
} from 'api/graphql/generated-bff';
import { getFontFamily } from './columnBannersShared';

interface HeroBannerData {
  badge?: HeroBannerBadge;
  content: HeroBannerContent;
  title: HeroBannerTitle;
  description: HeroBannerDescription;
  buttons: HeroBannerButton[];
  images: Record<'desktop' | 'tablet' | 'mobile', HeroBannerImage>[];
}

interface HeroBannerContent {
  backgroundColor?: string;
  copyBlockAlignment?: string;
  imageTreatment?: string;
}

interface HeroBannerTitle {
  text: string;
  textColor?: string;
  highlightTextColor?: string;
  fontFamily?: string;
  fontVariant?: string;
}

interface HeroBannerDescription {
  text: string;
  textColor?: string;
  fontVariant?: string;
}

interface HeroBannerButton {
  text: string;
  variant: string;
  action: () => void;
}

interface HeroBannerBadge {
  text: string;
  image?: string;
  alignment?: string;
  textColor?: string;
  backgroundColor?: string;
  outlineColor?: string;
  fontFamily?: string;
}

interface HeroBannerImage {
  width?: number;
  height?: number;
  url: string;
}

const mapBannerFields = (banner, onClickBannerCta) => {
  return {
    content: {
      imageTreatment: mapImageTreatmentCodename(banner.imageTreatment),
      copyBlockAlignment: mapAlignmentCodename(banner.copyBlockAlignment),
      backgroundColor: banner.bgColour,
    },
    title: {
      text: banner.title,
      textColor: banner.titleTextColour,
      highlightTextColor: banner.highlightTextColour,
      fontFamily: getFontFamily(banner.titleFont),
      fontVariant: mapFontSizeCodename(banner.titleFontSize),
    },
    description: {
      text: banner.description,
      textColor: banner.descriptionTextColour,
      fontVariant: mapFontSizeCodename(banner.descriptionFontSize),
    },
    badge:
      getCmsItemCodename(banner.displayBadge) === 'yes'
        ? {
            alignment: banner.badgeAlignment,
            text: banner.badgeText,
            textColor: banner.badgeTextColor,
            backgroundColor: banner.badgeBackgroundColor,
            outlineColor: banner.badgeOutlineColor,
          }
        : undefined,
    images: [
      {
        desktop: banner.imageDesktop,
        tablet: banner.imageTablet,
        mobile: banner.imageMobile,
      },
      banner.imageN2Desktop
        ? {
            desktop: getCmsItem(banner.imageN2Desktop),
            tablet: getCmsItem(banner.imageN2Tablet),
            mobile: getCmsItem(banner.imageN2Mobile),
          }
        : undefined,
    ],
    buttons: banner.buttons.map(btn => ({
      text: btn.cta_text,
      variant: mapButtonVariant(btn.cta_variant),
      action: () => onClickBannerCta(btn),
    })),
  };
};

export const transformStoreHeroBannerData = (banner, onClickBannerCta) => {
  return {
    content: {
      imageTreatment: mapImageTreatmentCodename(getCmsItemCodename(banner.imageTreatment)),
      copyBlockAlignment: mapAlignmentCodename(getCmsItemCodename(banner.copyBlockAlignment)),
      backgroundColor: banner.backgroundColour,
    },
    title: {
      text: banner.title,
      textColor: banner.titleTextColour,
      highlightTextColor: banner.highlightTextColour,
      fontFamily: getFontFamily(getCmsItemCodename(banner.titleFontStyle)),
      fontVariant: mapFontSizeCodename(getCmsItemCodename(banner.titleFontSize)),
    },
    description: {
      text: banner.description,
      textColor: banner.descriptionTextColour,
      fontVariant: mapFontSizeCodename(getCmsItemCodename(banner.descriptionFontSize)),
    },
    badge:
      getCmsItemCodename(banner.displayBadge) === 'yes'
        ? {
            alignment: getCmsItemCodename(banner.badgeAlignment),
            text: banner.badgeText,
            textColor: banner.badgeTextColour,
            backgroundColor: banner.badgeBackgroundColour,
            outlineColor: banner.badgeOutlineColour,
          }
        : undefined,
    images: [
      {
        desktop: banner.imageDesktop,
        tablet: banner.imageTablet.items[0],
        mobile: banner.imageMobile.items[0],
      },
      banner.image2Desktop
        ? {
            desktop: banner.image2Desktop.items[0],
            tablet: banner.image2Tablet.items[0],
            mobile: banner.image2Mobile.items[0],
          }
        : undefined,
    ],
    buttons: banner.buttons.items.map(btn => ({
      text: btn.ctaText,
      variant: mapButtonVariant(getCmsItemCodename(btn._ctaStyle.style)),
      action: () => onClickBannerCta(btn),
    })),
  };
};

export const transformHeroBannerBlockData = (
  heroBanner: CmsN1ColumnHeroBanner | CmsN2ColumnHeroBanner,
  onClickBannerCta: (btn: any) => void
) => {
  if ('leftGroup' in heroBanner && 'rightGroup' in heroBanner) {
    const { leftGroup, rightGroup } = heroBanner;
    return [leftGroup, rightGroup].map(banner => mapBannerFields(banner, onClickBannerCta));
  } else {
    return mapBannerFields(heroBanner, onClickBannerCta);
  }
};

export const getCmsItem = data => {
  if ('items' in data) return data.items[0];
  return data;
};

export const getCmsItemCodename = (data: CmsMultipleChoiceOptionCollection | string) => {
  if (typeof data === 'string') return data;
  return data?.items[0]._system_.codename;
};

const mapFontSizeCodename = (codename: string) => {
  switch (codename) {
    case 'xxl': {
      return 'display1';
    }
    case 'xl': {
      return 'display2';
    }
    case 'l': {
      return 'h1';
    }
    case 'm': {
      return 'h2';
    }
    case 'large': {
      return 'body1';
    }
    case 'regular': {
      return 'body2';
    }
  }
  return codename;
};

export const mapAlignmentCodename = (codename: string) => {
  switch (codename) {
    case 'centre': {
      return 'center';
    }
  }
  return codename;
};

export const mapImageTreatmentCodename = (codename: string) => {
  switch (codename) {
    case 'edge_to_edge': {
      return 'edge-to-edge';
    }
  }
  return codename;
};

export const mapButtonVariant = (buttonVariant: string) => {
  switch (buttonVariant) {
    case 'white_regular':
    case 'white_small': {
      return 'primaryInverted';
    }
    case 'white_regular_ghost':
    case 'white_small_ghost': {
      return 'secondaryInverted';
    }
    case 'primary_regular_ghost':
    case 'primary_small_ghost':
    case 'primary_small':
    case 'primary_regular': {
      return 'primary';
    }
    case 'video_play':
    case 'secondary_regular_ghost':
    case 'secondary_small_ghost':
    case 'secondary_small':
    case 'secondary_regular': {
      return 'secondary';
    }
    case 'tertiary_regular_ghost':
    case 'tertiary_small_ghost':
    case 'tertiary_small':
    case 'tertiary_regular': {
      return 'tertiary';
    }
  }
  return 'primary';
};
